import {
  Box,
  Progress as ChakraProgress,
  Flex,
  Heading,
  Stack,
  Text,
  useBreakpointValue,
  useDisclosure,
} from '@chakra-ui/react';
import { useRef } from 'react';
import { Outlet, useMatch, useNavigate, useParams } from 'react-router-dom';
import { useTranslations } from 'use-intl';

import { routes } from '@blockpulse3/data/shared';
import { FundraisingType, useDeleteFundraisingMutation } from '@blockpulse3/graphql/hooks';
import {
  DeleteDraftConfirmModal,
  Progress,
  ProgressStep,
  STEP_HEADER_HEIGHT_BASE,
  STEP_HEADER_HEIGHT_MD,
  StepHeader,
  useErrorToast,
  useSuccessToast,
} from '@blockpulse3/ui/commons';

import { useFundraisingSteps } from './useFundraisingLayoutSteps';

type Props = unknown;

/**
 * NewCrowdfundingFundraisingLayout.
 * Layout wrapping crowdfunding fundraising funnel steps and create components.
 *
 * @returns {JSX.Element}
 */
export function NewCrowdfundingFundraisingLayout(): JSX.Element {
  const i18nFundraisingLayout = useTranslations('NewFundraisingLayout');

  const errorToast = useErrorToast();
  const successToast = useSuccessToast();

  const top = useBreakpointValue({ base: STEP_HEADER_HEIGHT_BASE, md: STEP_HEADER_HEIGHT_MD });

  const navigate = useNavigate();
  const { fundraisingId = '', companyId = '' } = useParams();

  const draftConfirmRef = useRef(null);
  const draftConfirmModal = useDisclosure();

  const fundraisingRoutes = routes.workspace.fundraising.crowdfunding;

  const { progress, activeSteps } = useFundraisingSteps(
    fundraisingRoutes,
    FundraisingType.CROWDFUNDING,
  );

  const [deleteFundraising] = useDeleteFundraisingMutation();

  const isCreateActive = useMatch(fundraisingRoutes.create.full);

  const handleCloseNewFundraising = (): void => {
    navigate(routes.workspace.fmt(companyId));
  };

  const handleFundraisingDelete = (): void => {
    if (fundraisingId) {
      deleteFundraising({
        variables: {
          fundraisingId,
        },
        onCompleted: (res) => {
          if (res.deleteFundraising) {
            navigate(routes.workspace.fmt(companyId));
            successToast({ title: i18nFundraisingLayout('Toast.Success') });
          } else {
            errorToast({ title: i18nFundraisingLayout('Toast.Error') });
          }
        },
        onError: () => {
          errorToast({ title: i18nFundraisingLayout('Toast.Error') });
        },
      });
    }
  };

  return (
    <Flex direction="column" minH="100vh">
      <StepHeader
        p="4"
        showDelete={!isCreateActive}
        onClose={handleCloseNewFundraising}
        onDelete={draftConfirmModal.onOpen}
      >
        <Heading size="lg">{i18nFundraisingLayout('Title')}</Heading>
        <Text color="gray.600" fontWeight="normal">
          {i18nFundraisingLayout('SubTitle')}
        </Text>
      </StepHeader>
      <ChakraProgress
        height={1}
        max={activeSteps.length - 1}
        position="sticky"
        top={top}
        value={progress}
        zIndex={10}
        sx={{
          '&>div': {
            transition: 'width .7s ease',
          },
        }}
      />
      <Box bg="gray.50" flexGrow="1" h="full">
        <Stack direction={{ base: 'column', lg: 'row' }} p="6" spacing={{ base: 4, lg: 0 }}>
          <Flex flexBasis={{ base: 0, lg: 200 }} />
          <Progress>
            {activeSteps.map((step, i) => (
              <ProgressStep
                key={step.label}
                index={i}
                isActive={!!step.value}
                isCompleted={progress > i}
                title={step.label}
              />
            ))}
          </Progress>
          <Flex flexGrow="1" maxW="3xl">
            <Outlet />
          </Flex>
        </Stack>
      </Box>
      <DeleteDraftConfirmModal
        isOpen={draftConfirmModal.isOpen}
        leastDestructiveRef={draftConfirmRef}
        onClose={draftConfirmModal.onClose}
        onDelete={handleFundraisingDelete}
      />
    </Flex>
  );
}

export type NewCrowdfundingFundraisingLayoutProps = Props;
